import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tag, Button, Card, Descriptions, message, Select } from 'antd';
import axios from 'axios';
import { getRide, getRideHistory } from '../../../../store/rides';
import { Navigation } from '../../../../utils/navigation';
import { USER_ROLES, UBER_TEXT } from '../../../../constants/constants';
import {
  PhoneOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import styles from './index.module.css';
import UberHealthIcon from '../../../../assets/svgs/uberHealth.svg';

const UBER_STATUS_LABELS = {
  processing: 'Processing',
  scheduled: 'Scheduled',
  arriving: 'Driver Arriving',
  en_route_to_pickup: 'En Route to Pickup',
  arrived_at_pickup: 'Arrived at Pickup',
  on_trip: 'On Trip',
  arrived_at_dropoff: 'Arrived at Dropoff',
  completed: 'Completed',
  canceled: 'Canceled',
  rider_canceled: 'Rider Canceled',
  driver_canceled: 'Driver Canceled',
  no_show: 'No Show',
  receipt_ready: 'Receipt Ready',
};

const UBER_STATUS_COLORS = {
  processing: 'blue',
  scheduled: 'cyan',
  arriving: 'orange',
  en_route_to_pickup: 'orange',
  arrived_at_pickup: 'gold',
  on_trip: 'green',
  arrived_at_dropoff: 'green',
  completed: 'success',
  canceled: 'error',
  rider_canceled: 'error',
  driver_canceled: 'error',
  no_show: 'warning',
  receipt_ready: 'purple',
};

export const UberRideInfo = ({
  rideId,
  uberInfo,
  onTrackDriver,
  rideReceipt,
  uberReceipt,
  uberReceiptData,
  showUberRideId = false,
}) => {
  const user = useSelector(state => state.authorization.user);
  const dispatch = useDispatch();
  const navigation = Navigation();

  const [simulating, setSimulating] = useState(false);
  const [simulationStatus, setSimulationStatus] = useState('completed');

  if (!uberInfo) return null;

  const { uberRideId, uberStatus, driver, lastLocation, driverWaitTimeMinutes } = uberInfo;
  const normalizedStatus = typeof uberStatus === 'string' ? uberStatus : '';
  const statusLabel = UBER_STATUS_LABELS[normalizedStatus] || normalizedStatus || 'Unknown';
  const statusColor = UBER_STATUS_COLORS[normalizedStatus] || 'default';

  const canTrackDriver =
    driver &&
    lastLocation &&
    ['arriving', 'en_route_to_pickup', 'arrived_at_pickup', 'on_trip'].includes(normalizedStatus);

  const isDevOrStage = ['localhost', 'dev', 'stage'].some((envToken) =>
    window.location.hostname.includes(envToken)
  );

  const handleSimulateStatusChange = async () => {
    setSimulating(true);
    try {
      let eventType = 'rides.status_changed';
      if (simulationStatus === 'completed') {
        eventType = 'health.completed';
      } else if (simulationStatus === 'receipt_ready') {
        eventType = 'health.receipt_ready';
      }

      const payload = {
        event_type: eventType,
        event_id: Date.now().toString(),
        event_time: new Date().toISOString(),
        meta: {
          user_id: user?.id || 'test-user-id',
          resource_id: uberRideId,
          status: simulationStatus,
        },
        resource_href: `https://api.uber.com/v1/health/trips/${uberRideId}`,
      };

      // Webhook endpoint is public (no JWT required) — use raw axios
      const apiUrl = process.env.REACT_APP_API_URL || '';
      await axios.post(`${apiUrl}/webhooks/uber-health`, payload, {
        headers: { 'x-uber-signature': 'test-signature' },
        withCredentials: false,
      });

      message.success(`Uber status '${simulationStatus}' simulated successfully!`);
      dispatch(getRide(rideId, navigation));
      dispatch(getRideHistory(rideId));
    } catch (error) {
      console.error(error);
      const errorMessage = error.response?.data?.message || error.message;
      message.error(`Failed to simulate Uber status change: ${errorMessage}`);
    } finally {
      setSimulating(false);
    }
  };

  return (
    <>
      <Card
        title={
          <div className={styles.uberCardTitle}>
            <span>{UBER_TEXT.RIDE_INFO_TITLE}</span>
            <span className={styles.uberHealthTag}>
              <img src={UberHealthIcon} alt="Uber Health" className={styles.uberHealthIcon} />
              Uber Health
            </span>
          </div>
        }
        className={styles.uberCard}
        size="small"
      >
        <Descriptions column={1} size="small">
          {showUberRideId && (
            <Descriptions.Item label="Uber Ride ID">
              <code>{uberRideId}</code>
            </Descriptions.Item>
          )}

          <Descriptions.Item label="Status">
            <Tag
              color={statusColor}
              icon={
                normalizedStatus === 'completed' ? (
                  <CheckCircleOutlined />
                ) : normalizedStatus.includes('cancel') ? (
                  <InfoCircleOutlined />
                ) : (
                  <ClockCircleOutlined />
                )
              }
            >
              {statusLabel}
            </Tag>
          </Descriptions.Item>

          {driver && (
            <>
              <Descriptions.Item label="Driver Name">{driver.name}</Descriptions.Item>

              <Descriptions.Item label="Driver Phone">
                <PhoneOutlined style={{ marginRight: '4px' }} />
                {driver.phone || "Not specified"}
              </Descriptions.Item>

              <Descriptions.Item label="Vehicle">
                {driver.vehicle || 'Not specified'}
              </Descriptions.Item>
            </>
          )}

          {canTrackDriver && (
            <Descriptions.Item>
              <Button
                type="primary"
                icon={<EnvironmentOutlined />}
                onClick={() => onTrackDriver?.(rideId)}
                block
              >
                Track Driver in Real-Time
              </Button>
            </Descriptions.Item>
          )}

          {!driver && normalizedStatus === 'scheduled' && (
            <Descriptions.Item>
              <div className={styles.infoMessage}>
                <InfoCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                {UBER_TEXT.DRIVER_ASSIGNMENT_INFO}
              </div>
            </Descriptions.Item>
          )}

          {isDevOrStage && user?.role === USER_ROLES.SUPER_ADMIN && (
            <Descriptions.Item>
              <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                <Select
                  value={simulationStatus}
                  style={{ width: '100%' }}
                  onChange={(val) => setSimulationStatus(val)}
                  options={Object.keys(UBER_STATUS_LABELS).map((key) => ({
                    value: key,
                    label: UBER_STATUS_LABELS[key]
                 }))}
               />
               <Button 
                loading={simulating}
                onClick={handleSimulateStatusChange}
                type="dashed"
                danger
                icon={<ThunderboltOutlined />}
                block
               >
                 Simulate Update (Dev/Stage Only)
               </Button>
             </div>
          </Descriptions.Item>
        )}

      </Descriptions>
    </Card>
    </>
  );
};
